<script lang="ts">
	// services
	import { format } from 'svelte-i18n';

	// helpers
	import { selectWeatherConditions } from './helpers/common-helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { isInvalidWeatherCondition } from 'src/components/Scenes/helpers/has_missing_scene_data';

	// components
	import { _init_weather_components } from 'src/components/Scenes/helpers/init_weather_conditions';
	import { generateNegativeInputValueError } from 'src/components/Scenes/helpers/init_device_conditions';
	import { WHEN_WIZARD_STEPS_INDEXES } from '../../helpers/when-wizard-helpers';

	import { is_weather_cond_ui } from 'src/types/WeatherConditionUI';

	// shelly design system
	import { Text, Select, Token, Alert } from '@Shelly/shelly-design-system';
	import CustomDynamicInput from 'src/components/CustomDynamicInput.svelte';

	export let sceneData: any;

	// condition configuration
	const translatedTitleWeatherOption = {
		clouds: {
			translation: $format('weather_title_weather_type', {
				default: 'Weather type',
			}),
			options_translation_prefix: 'label_',
			icon: 'fas fa-cloud',
			unit: '',
			default_value: '',
		},
		wind_direction: {
			translation: $format('weather_title_wind_direction', {
				default: 'When the wind blows from',
			}),
			options_translation_prefix: 'label_',
			icon: 'fas fa-wind',
			unit: '',
			default_value: '',
		},
		wind_force: {
			translation: $format('weather_title_wind_force', {
				default: 'When the wind force is',
			}),
			options_translation_prefix: 'option_',
			icon: 'fas fa-wind',
			unit: '',
			default_value: '',
		},
		wind_speed: {
			translation: $format('cond_when_weather_wind_speed_is_m_s', {
				default: 'When the wind speed is',
			}),
			icon: 'fas fa-wind',
			unit: 'km/h',
			default_value: '0',
		},
		chance_precipitation: {
			translation: $format('weather_title_chance_precipitation', {
				default: 'Chance of precipitation is',
			}),
			icon: 'fas fa-cloud-rain',
			unit: '%',
			default_value: '50',
		},
		temperature: {
			translation: $format('weather_title_temperature', {
				default: 'Temperature is',
			}),
			icon: 'fas fa-thermometer-half',
			unit: '°C',
			default_value: '0',
		},
		humidity: {
			translation: $format('weather_title_humidity', {
				default: 'Humidity is',
			}),
			icon: 'fas fa-tint',
			unit: '%',
			default_value: '60',
		},
	};

	let inputValError: string = '';
	let isValidInputValue: boolean = false;

	const weatherOptions = _init_weather_components();

	let selectedWeatherCondition = {
		value: undefined,
		title: '',
	};

	let weatherConditionOptions = selectWeatherConditions(weatherOptions);

	let selectedWeatherOption: any = {
		id: -1,
		cond: '',
		i18n: '',
	};

	$: hasMissingData = isInvalidWeatherCondition({
		condition: selectedWeatherOption?.cond,
		value: sceneData?.scene?.thsval || sceneData?.scene?.eq?.[1],
	}); //this is used to add a warning alert if the weather condition is not set correctly

	const createSelectedWeatherOptionTranslated = (value, cond) => {
		const prefix = translatedTitleWeatherOption[cond].options_translation_prefix;
		return {
			// TODO ADD TRANSLATIONS
			title: $format(`${prefix}${value}`, {
				default: capFirstChar(value),
			}),
			value: value,
		};
	};

	const setSelectedWeatherOption = (weatherCondition) => {
		const foundWeatherOption = weatherOptions.find((el) => el.cond === weatherCondition);

		if (!foundWeatherOption?.options) return foundWeatherOption;

		const mappedWeatherOptions = foundWeatherOption.options.map((option) => {
			return createSelectedWeatherOptionTranslated(option, weatherCondition);
		});

		return {
			...foundWeatherOption,
			options: mappedWeatherOptions,
		};
	};

	if (is_weather_cond_ui(sceneData.translated)) {
		sceneData.weatherCondition = { ...sceneData.translated };

		selectedWeatherOption = setSelectedWeatherOption(sceneData.weatherCondition.cond);

		selectedWeatherCondition = {
			title: $format(selectedWeatherOption.i18n),
			value: selectedWeatherOption.cond,
		};
	}

	const validateWizardStepSelectOption = () => {
		sceneData.validStepIndex = WHEN_WIZARD_STEPS_INDEXES.WEATHER_STEP;
		sceneData.isValid = false;
	};

	const validateWizardSelectCondition = () => {
		if (sceneData.validStepIndex !== WHEN_WIZARD_STEPS_INDEXES.WEATHER_STEP) {
			return;
		}

		let found = weatherOptions.find((el) => {
			let isEqualCond = el.cond === sceneData.weatherCondition.cond;
			let includesOperator = el?.ui_operators?.includes(sceneData.weatherCondition.op);
			return (
				isEqualCond &&
				includesOperator &&
				(typeof isValidInputValue === 'boolean'
					? isValidInputValue
					: !!isValidInputValue && +isValidInputValue >= 0)
			);
		});

		sceneData.isValid = found ? true : false;
	};

	const onChangeWeatherCondition = (weatherCondition) => {
		validateWizardStepSelectOption();

		selectedWeatherCondition = weatherCondition;
		selectedWeatherOption = setSelectedWeatherOption(selectedWeatherCondition.value);

		sceneData.weatherCondition = {
			...sceneData.weatherCondition, //save execution_type and hours
			cond: selectedWeatherOption.cond,
			value: translatedTitleWeatherOption[selectedWeatherOption.cond].default_value,
			op: '',
			locid: '',
		};
	};

	const onChangeWeatherOption = (option) => {
		validateWizardStepSelectOption();

		sceneData.weatherCondition = {
			...sceneData.weatherCondition,
			...option,
		};
	};

	$: {
		({ inputValError, isValidInputValue } = generateNegativeInputValueError(
			sceneData.weatherCondition,
		));

		if (!inputValError && sceneData.weatherCondition.cond !== 'temperature') {
			inputValError =
				!sceneData.weatherCondition.value || isNaN(+sceneData.weatherCondition.value)
					? $format('value_must_be_equal_or_greater_than_zero', {
							default: 'Value must be an integer and equal or greater than 0.',
						})
					: undefined;
		}
	}

	$: (sceneData, validateWizardSelectCondition());

	let inputHandler = (e) =>
		onChangeWeatherOption({
			value: e.target.value,
		});

	let minusFunction = () =>
		onChangeWeatherOption({
			value:
				sceneData.weatherCondition.cond === 'temperature' ||
				(!inputValError && +sceneData.weatherCondition.value > 0)
					? `${+sceneData.weatherCondition.value - 1}`
					: '0',
		});

	let plusFunction = () =>
		onChangeWeatherOption({
			value:
				sceneData.weatherCondition.cond === 'temperature' || !inputValError
					? `${+sceneData.weatherCondition.value + 1}`
					: '0',
		});
</script>

<!-- condition type selection -->

<Text element="h2" class="mb-s">
	{capFirstChar(
		$format('select_option', {
			default: 'Select option',
		}),
	)}
</Text>

<div class="d-flex flex-wrap" style="gap:var(--spacing-s)">
	{#each weatherConditionOptions as weatherCondition}
		<Token
			id="token-kmwfirn"
			title={weatherCondition.title}
			class="text-capitalize"
			on:click={() => onChangeWeatherCondition(weatherCondition)}
			selected={selectedWeatherCondition.value === weatherCondition.value}
		/>
	{/each}
</div>

<!-- Options are rended after condition type is selected -->

<Text styleType="h2" class="mb-s mt-l">
	{capFirstChar($format(selectedWeatherOption.i18n))}
</Text>

<!-- //for clouds add tokens -->
{#if selectedWeatherCondition.value !== undefined && selectedWeatherOption.cond === 'clouds'}
	{#if hasMissingData}
		<Alert
			type="warning"
			class="mb-s"
			heading={$format('weather_forecast_invalid_data', {
				default: 'Invalid weather forecast data',
			})}
		/>
	{/if}
	<div class="d-flex flex-wrap" style="gap:var(--spacing-s)">
		{#each selectedWeatherOption.options as option}
			<Token
				id="token-0dnqzke"
				title={option.title}
				size="m"
				class="text-capitalize"
				selected={sceneData.weatherCondition.value === option.value}
				on:click={() => {
					onChangeWeatherOption({
						value: option.value,
						op: selectedWeatherOption['ui_operators'][0],
					});
				}}
			/>
		{/each}
	</div>
{/if}

<!-- //for wind_direction and wind_force add a drop down select -->
{#if selectedWeatherCondition.value !== undefined && (selectedWeatherOption.cond === 'wind_direction' || selectedWeatherOption.cond === 'wind_force')}
	<Select
		aria-labelledby="select-xooxwwk"
		label={$format('select_option', {
			default: 'Select option',
		})}
		icon=""
		options={selectedWeatherOption.options}
		value={createSelectedWeatherOptionTranslated(
			sceneData.weatherCondition.value,
			sceneData.weatherCondition.cond,
		)}
		on:change={(e) => {
			onChangeWeatherOption({
				value: e.detail.value,
				op: selectedWeatherOption['ui_operators'][0],
			});
		}}
		id="select-xooxwwk"
	/>
{/if}

<!-- //for chance_precipitation, temperature and humidity add a number input -->
{#if selectedWeatherCondition.value !== undefined && !selectedWeatherOption.options}
	<div class="d-flex flex-wrap mb-s" style="gap:var(--spacing-s)">
		{#each selectedWeatherOption.ui_operators as operator}
			<Token
				id="token-1q2nuv6"
				icon={operator === 'less_than' ? 'fas fa-arrow-down' : 'fas fa-arrow-up'}
				size="m"
				title={$format(`condition_operator_${operator}`, {
					values: {
						value: sceneData.weatherCondition.value,
						units: translatedTitleWeatherOption[selectedWeatherOption.cond].unit,
					},
					default: `${capFirstChar(operator.split('_').join(' '))} ${sceneData.weatherCondition.value} ${translatedTitleWeatherOption[selectedWeatherOption.cond].unit}`,
				})}
				selected={sceneData.weatherCondition.op === operator}
				on:click={() => {
					onChangeWeatherOption({
						op: operator,
					});
				}}
			/>
		{/each}
	</div>

	<CustomDynamicInput
		additionalInputAttributes={{
			icon: translatedTitleWeatherOption[selectedWeatherOption.cond].icon,
		}}
		disabledMinusBtn={['humidity', 'chance_precipitation'].includes(selectedWeatherOption.cond) &&
			+sceneData.weatherCondition.value <= 0}
		disabledPlusBtn={['humidity', 'chance_precipitation'].includes(selectedWeatherOption.cond) &&
			+sceneData.weatherCondition.value >= 100}
		bind:value={sceneData.weatherCondition.value}
		bind:error={inputValError}
		bind:inputHandler
		bind:minusFunction
		bind:plusFunction
	/>
{/if}
